/**
 * Cookie consent (views/home/consent.php).
 *
 * The decision is stored for 180 days in the first-party `p26_consent`
 * cookie as {v, necessary, preferences, analytics, marketing, ts}. Until it
 * exists the dialog opens on every page. After a decision it is exposed as
 * window.portale2026Consent, forwarded to Google Consent Mode when gtag is
 * present, and announced with a `portale2026:consent` event, so tag and ad
 * scripts can wait for it.
 */
const COOKIE = 'p26_consent';
const MAX_AGE = 60 * 60 * 24 * 180;

const read = () => {
  const m = document.cookie.match(/(?:^|; )p26_consent=([^;]*)/);
  if (!m) return null;
  try { return JSON.parse(decodeURIComponent(m[1])); } catch { return null; }
};

const write = (consent) => {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(consent))}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

const apply = (consent) => {
  window.portale2026Consent = consent;
  if (typeof window.gtag === 'function') {
    const state = (on) => (on ? 'granted' : 'denied');
    window.gtag('consent', 'update', {
      analytics_storage: state(consent.analytics),
      ad_storage: state(consent.marketing),
      ad_user_data: state(consent.marketing),
      ad_personalization: state(consent.marketing),
      personalization_storage: state(consent.preferences),
    });
  }
  document.dispatchEvent(new CustomEvent('portale2026:consent', { detail: consent }));
};

export function initConsent() {
  const dialog = document.getElementById('hp-consent');
  if (!dialog) return;

  let returnFocus = null;

  const open = () => {
    returnFocus = document.activeElement;
    const current = read() || {};
    dialog.querySelectorAll('[data-consent-key]').forEach((input) => { input.checked = !!current[input.dataset.consentKey]; });
    dialog.dataset.view = 'intro';
    dialog.hidden = false;
    document.documentElement.classList.add('is-consent-open');
    dialog.querySelector('[data-consent-accept]')?.focus();
    unlockIfHidden();
  };

  // The open dialog stops the page scrolling (_consent.scss). If something
  // hides it — a "cookie notice" filter list, a reader's element picker —
  // the reader could neither answer it nor scroll: give the page back.
  const unlockIfHidden = () => {
    const check = () => {
      if (dialog.hidden || !document.documentElement.classList.contains('is-consent-open')) return;
      const style = getComputedStyle(dialog);
      if (!dialog.getClientRects().length || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        document.documentElement.classList.remove('is-consent-open');
      }
    };
    requestAnimationFrame(check);
    setTimeout(check, 1500); // extensions may apply their styles a moment later
  };

  const close = () => {
    dialog.hidden = true;
    document.documentElement.classList.remove('is-consent-open');
    if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
  };

  const save = (choice) => {
    const consent = {
      v: 1,
      necessary: true,
      preferences: !!choice.preferences,
      analytics: !!choice.analytics,
      marketing: !!choice.marketing,
      ts: Date.now(),
    };
    write(consent);
    apply(consent);
    close();
  };

  dialog.addEventListener('click', (e) => {
    const control = e.target.closest('[data-consent-accept], [data-consent-reject], [data-consent-customize], [data-consent-save]');
    if (!control) return;

    if (control.matches('[data-consent-accept]')) {
      save({ preferences: true, analytics: true, marketing: true });
    } else if (control.matches('[data-consent-reject]')) {
      save({});
    } else if (control.matches('[data-consent-customize]')) {
      dialog.dataset.view = 'settings';
      dialog.querySelector('[data-consent-key]')?.focus();
    } else {
      const choice = {};
      dialog.querySelectorAll('[data-consent-key]').forEach((input) => { choice[input.dataset.consentKey] = input.checked; });
      save(choice);
    }
  });

  // Keep focus inside while open (the reader has to decide).
  dialog.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = [...dialog.querySelectorAll('a[href], button, input:not([disabled])')].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-consent-open], .hp-mcookie')) {
      e.preventDefault();
      open();
    }
  });

  const current = read();
  if (current) apply(current);
  else open();
}
